/**
 * Tenant entity — mirrors `leviathan_master.tenants` and the DynamoDB tenant registry
 * (test: `test-leviathan-tenant-registry`, prod: `leviathan-tenant-registry`).
 */
import { z } from 'zod';
import { ISO8601Date, TenantId, TenantSlug } from '../primitives/index.js';
import { PlanTier, TenantStatus, Vertical } from '../enums/index.js';
/**
 * Tenant settings (JSON column). Intentionally loose: settings shape evolves
 * per-vertical. Consumers should narrow with their own Zod schema.
 */
export const TenantSettingsSchema = z.record(z.string(), z.unknown());
export const TenantSchema = z.object({
    id: TenantId,
    slug: TenantSlug,
    name: z.string().min(1).max(255),
    plan: PlanTier,
    status: TenantStatus,
    /** Template/vertical identifier; drives DataModel choice at provisioning. */
    vertical: Vertical,
    /** Normalised MySQL schema name for this tenant (derived from slug). */
    mysqlSchema: z.string().min(1).max(64),
    trialEndsAt: ISO8601Date.nullable(),
    settings: TenantSettingsSchema.nullable(),
    createdAt: ISO8601Date,
    updatedAt: ISO8601Date,
});
/** Minimal tenant projection for listings and cross-tenant dropdowns. */
export const TenantSummarySchema = TenantSchema.pick({
    id: true,
    slug: true,
    name: true,
    plan: true,
    status: true,
    vertical: true,
});
export const TenantCreateSchema = z.object({
    slug: TenantSlug,
    name: z.string().min(1).max(255),
    plan: PlanTier.default('starter'),
    vertical: Vertical,
    /** Optional owner email — creates the first user alongside the tenant. */
    ownerEmail: z.email().max(254).optional(),
    ownerName: z.string().min(1).max(100).optional(),
    settings: TenantSettingsSchema.optional(),
});
export const TenantUpdateSchema = z
    .object({
    name: z.string().min(1).max(255),
    plan: PlanTier,
    status: TenantStatus,
    trialEndsAt: ISO8601Date.nullable(),
    settings: TenantSettingsSchema.nullable(),
})
    .partial();
//# sourceMappingURL=tenant.js.map